import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Tabs, Tab, Button, IconButton, Dialog } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import QuizResultsTab from './QuizResultsTab';
import TechnicalConceptsTab from './TechnicalConceptsTab';
import CodingQuestionTab from './CodingQuestionTab';
import { getSubmissionDetails, saveMentorFeedback, verifyMentorPractices } from '../../../../services/mentorServices/LearnerPracticeServices';
import { toast } from 'react-toastify';
import './SubmissionDetails.css';

const SubmissionDetails = ({ isAdmin = false }) => {
    const navigate = useNavigate();
    const { submissionId, coeName } = useParams();
    const location = useLocation();
    const learnerId = location.state?.learnerId;

    const getBasePracticePath = () => {
        if (coeName) return `/${coeName}/learner-practices`;
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (user?.role === 'coe_report_viewer') {
            const formattedCoeName = user?.coeName ? user.coeName.trim().replace(/\s+/g, '-') : (user?.coe_name ? user.coe_name.trim().replace(/\s+/g, '-') : 'coe');
            return `/${formattedCoeName}/learner-practices`;
        }
        return isAdmin ? '/admin/learner-practices' : '/mentor/learner-practices';
    };

    const [activeTab, setActiveTab] = useState(0);
    const [showWarning, setShowWarning] = useState(true);
    const [openVerifyModal, setOpenVerifyModal] = useState(false);

    const [submissionData, setSubmissionData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [feedbackState, setFeedbackState] = useState({});
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (learnerId && submissionId) {
            fetchSubmissionDetails();
        }
    }, [learnerId, submissionId]);

    const fetchSubmissionDetails = async () => {
        setLoading(true);
        try {
            const res = await getSubmissionDetails(learnerId, submissionId);
            const responseData = res?.data;
            if (responseData?.success) {
                setSubmissionData(responseData.data);
            } else {
                toast.error(responseData?.message || "Failed to fetch submission details.");
            }
        } catch (error) {
            console.error("Failed to fetch submission details:", error);
            toast.error(error?.response?.data?.message || "Error fetching submission details.");
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFeedbackChange = (evaluationId, score, comment) => {
        if (!evaluationId) return;
        setFeedbackState(prev => {
            const current = prev[evaluationId] || {};
            return {
                ...prev,
                [evaluationId]: {
                    evaluationId,
                    score: score !== undefined ? score : current.score,
                    feedback: comment !== undefined ? comment : current.feedback
                }
            };
        });
    };

    const handleSave = async (isVerifyFlow = false) => {
        const rawFeedbacks = Object.values(feedbackState);
        if (rawFeedbacks.length === 0) {
            if (!isVerifyFlow) toast.info("No changes to save.");
            return true;
        }

        try {
            const questionFeedbacks = rawFeedbacks.map(item => ({
                evaluationId: item.evaluationId,
                ...(item.score !== undefined && { score: item.score }),
                ...(item.feedback !== undefined && { feedback: item.feedback })
            }));
            const payload = { questionFeedbacks };
            const res = await saveMentorFeedback(learnerId, submissionId, payload);
            if (res?.data?.success) {
                if (!isVerifyFlow) {
                    toast.success("Feedback saved successfully.");
                    fetchSubmissionDetails();
                }
                setFeedbackState({}); // Clear after saving
                return true;
            } else {
                toast.error(res?.data?.message || "Failed to save feedback.");
                return false;
            }
        } catch (error) {
            console.error("Save error:", error);
            toast.error(error?.response?.data?.message || "Error saving feedback.");
            return false;
        }
    };

    const handleVerify = async () => {
        setVerifying(true);
        try {
            const saveSuccess = await handleSave(true);
            if (!saveSuccess) {
                setVerifying(false);
                return;
            }

            const payload = {
                Practices: [
                    {
                        learnerId: learnerId,
                        PracticeProgressId: submissionId
                    }
                ]
            };
            const res = await verifyMentorPractices(payload);
            if (res?.data?.success) {
                toast.success("Practice marked as verified successfully.");
                setOpenVerifyModal(false);
                fetchSubmissionDetails();
            } else {
                toast.error(res?.data?.message || "Failed to verify Practice.");
            }
        } catch (error) {
            console.error("Verify error:", error);
            toast.error(error?.response?.data?.message || "Error verifying Practice.");
        } finally {
            setVerifying(false);
        }
    };

    const selectedLearners = location.state?.selectedLearners || [];
    const currentIndex = selectedLearners.findIndex(l => l.submissionId === submissionId);
    const hasBulkNavigation = selectedLearners.length > 1 && currentIndex !== -1;

    const handleNavigateBulk = (direction) => {
        if (!hasBulkNavigation) return;
        const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex >= 0 && newIndex < selectedLearners.length) {
            const target = selectedLearners[newIndex];
            navigate(`${getBasePracticePath()}/${target.submissionId}`, {
                state: {
                    learnerId: target.learnerId,
                    selectedLearners: selectedLearners
                }
            });
        }
    };

    const isCompleted = submissionData?.progress?.status?.toLowerCase() === 'completed';

    return (
        <Box className="submission-details-container">
            <Box className="submission-details-breadcrumb">
                <span className="breadcrumb-link" onClick={() => navigate(getBasePracticePath())}>Learner Practice</span> &gt; Practice
            </Box>

            <Box className="submission-details-header">
                <Box sx={{ width: '100%' }}>
                    <Box className="submission-details-title-row" sx={{ justifyContent: 'space-between', width: '100%', marginBottom: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <Typography className="submission-details-title">
                                {submissionData?.practice?.title || "Loading..."}
                            </Typography>
                            <span className={`status-chip ${submissionData?.progress?.status?.toLowerCase() === 'completed' ? 'Practiceed' : 'pending'}`} style={{ padding: '3px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '12px' }}>
                                {submissionData?.progress?.status ? submissionData.progress.status.replace(/_/g, ' ') : "PENDING"}
                            </span>
                        </Box>
                        
                        {hasBulkNavigation && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#6c757d' }}>
                                <Button 
                                    onClick={() => handleNavigateBulk('prev')} 
                                    disabled={currentIndex === 0}
                                    disableRipple
                                    sx={{ minWidth: 'auto', padding: 0, color: currentIndex === 0 ? '#ced4da' : '#1976d2', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, '&:hover': { backgroundColor: 'transparent', color: currentIndex === 0 ? '#ced4da' : '#115293' } }}
                                >
                                    &lt; Prev
                                </Button>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#495057' }}>
                                    Practice {currentIndex + 1} OF {selectedLearners.length}
                                </Typography>
                                <Button 
                                    onClick={() => handleNavigateBulk('next')} 
                                    disabled={currentIndex === selectedLearners.length - 1}
                                    disableRipple
                                    sx={{ minWidth: 'auto', padding: 0, color: currentIndex === selectedLearners.length - 1 ? '#ced4da' : '#1976d2', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, '&:hover': { backgroundColor: 'transparent', color: currentIndex === selectedLearners.length - 1 ? '#ced4da' : '#115293' } }}
                                >
                                    Next &gt;
                                </Button>
                            </Box>
                        )}
                    </Box>

                </Box>
            </Box>

            {showWarning && submissionData?.progress?.status?.toLowerCase() !== 'completed' && (
                <Box className="Practice-warning-banner">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <InfoOutlinedIcon sx={{ color: '#f57c00', fontSize: '20px', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: '#495057', fontSize: '13.5px' }}>
                            This Practice will only be visible to the learner after you complete the verification of the Quiz, Concepts, Coding Results, and submit the L&amp;C parameters.
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setShowWarning(false)} sx={{ color: '#6c757d', padding: '4px', flexShrink: 0, '&:hover': { color: '#212529', backgroundColor: 'rgba(0,0,0,0.05)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <Box className="submission-top-box">
                <Box className="submission-learner-info" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Avatar sx={{ width: 52, height: 52, border: '2px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} src={submissionData?.learner?.image || "/static/images/avatar/1.jpg"} alt={submissionData?.learner?.name} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Typography variant="h6" sx={{ fontSize: '17px', fontWeight: 700, color: '#212529', lineHeight: 1.2 }}>
                                <span style={{ textTransform: 'capitalize' }}>{submissionData?.learner?.name || "Loading..."}</span>
                                {submissionData?.learner?.rollNo ? <span style={{ fontWeight: 500, fontSize: '13px', color: '#495057' }}>{` - ${submissionData.learner.rollNo}`}</span> : ''}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: '#495057', fontSize: '13px', fontWeight: 500 }}>
                                {[
                                    submissionData?.learner?.email,
                                    submissionData?.learner?.coeName,
                                    submissionData?.learner?.labName
                                ].filter(Boolean).join(' - ')}
                            </Typography>
                        </Box>

                        {submissionData?.practice?.practiceName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32', backgroundColor: '#e8f5e9', padding: '3px 10px', borderRadius: '4px', border: '1px solid #c8e6c9', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <VerifiedIcon sx={{ fontSize: '14px' }} />
                                    Practice: {submissionData.practice.practiceName}
                                </span>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '50px' }}>
                    <Box className="top-box-section" sx={{ borderLeft: '1px solid #e0e0e0', paddingLeft: '50px' }}>
                        <span className="top-box-label">SUBMISSION DATE</span>
                        <span className="top-box-value">
                            {submissionData?.progress?.submittedAt ? new Date(submissionData.progress.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "--"}
                        </span>
                    </Box>
                    <Box className="top-box-section" sx={{ borderLeft: '1px solid #e0e0e0', paddingLeft: '50px' }}>
                        <span className="top-box-label">CURRENT STATUS</span>
                        <span className="top-box-value" style={{ textTransform: 'uppercase' }}>
                            <span className={`status-dot ${submissionData?.progress?.status?.toLowerCase() === 'completed' ? 'Practiceed' : 'pending'}`}></span>
                            {submissionData?.progress?.status ? submissionData.progress.status.replace(/_/g, ' ').toUpperCase() : "PENDING"}
                        </span>
                    </Box>
                </Box>
            </Box>



            <Box sx={{ borderBottom: '1px solid #e0e0e0', marginBottom: '20px' }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Quiz Results" sx={{ textTransform: 'none', fontWeight: activeTab === 0 ? 700 : 500 }} />
                    <Tab label="Concept Results" sx={{ textTransform: 'none', fontWeight: activeTab === 1 ? 700 : 500 }} />
                    <Tab label="Coding Results" sx={{ textTransform: 'none', fontWeight: activeTab === 2 ? 700 : 500 }} />
                </Tabs>
            </Box>

            <Box className="main-content-area">
                <Box className="tabs-container">
                    <Box>
                        {activeTab === 0 && <QuizResultsTab data={submissionData?.evaluations?.quiz} onFeedbackChange={handleFeedbackChange} readOnly={isCompleted} />}
                        {activeTab === 1 && <TechnicalConceptsTab data={submissionData?.evaluations?.technical} onFeedbackChange={handleFeedbackChange} readOnly={isCompleted} />}
                        {activeTab === 2 && <CodingQuestionTab data={submissionData?.evaluations?.coding} onFeedbackChange={handleFeedbackChange} readOnly={isCompleted} />}
                    </Box>
                </Box>
            </Box>



            {/* Mark as Verified Modal */}
            <Dialog open={openVerifyModal} onClose={() => setOpenVerifyModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '8px', overflow: 'hidden' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>Mark as Verified</Typography>
                    <IconButton size="small" onClick={() => setOpenVerifyModal(false)} sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ padding: '24px' }}>
                    <Box sx={{ display: 'flex', gap: '12px', backgroundColor: '#fff8eb', border: '1px solid #ffe0b2', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                        <ReportProblemOutlinedIcon sx={{ color: '#f57c00', fontSize: '22px', flexShrink: 0, marginTop: '2px' }} />
                        <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.5 }}>
                            Please confirm that you have manually verified the Quiz, Concept, and Coding results, and have added and saved your feedback and justification.
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6 }}>
                        Once you mark this Practice as Verified, it will be locked, and you will no longer be able to make any changes to this learner's Practice.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setOpenVerifyModal(false)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#495057', padding: '6px 16px', borderRadius: '6px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleVerify}
                        disabled={verifying}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#0056b3', color: '#fff', padding: '6px 20px', borderRadius: '6px', '&:hover': { backgroundColor: '#004085' } }}
                    >
                        {verifying ? "Confirming..." : "Confirm"}
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export default SubmissionDetails;
